/**
 * Tela Configurações.
 *
 * - API URL: persistida em localStorage.
 * - Admin Token: persistido em localStorage (NÃO é segredo de produção
 *   neste sprint — Sprint 1 ainda usa token estático).
 * - Refresh interval: 3–300 segundos.
 * - Botão "testar conexão" chama GET /health.
 */
import { useEffect, useState } from "react";
import NavIcon from "../nav/NavIcon.jsx";
import { getHealth, getSecuritySummary } from "../lib/api.js";
import { clearConfig, loadConfig, saveConfig } from "../lib/config.js";
import { t } from "../i18n/index.js";

export default function Settings({ onSaved }) {
  const initial = loadConfig();
  const [apiUrl, setApiUrl] = useState(initial.apiUrl);
  const [adminToken, setAdminToken] = useState(initial.adminToken);
  const [refreshSeconds, setRefreshSeconds] = useState(initial.refreshSeconds);
  const [showToken, setShowToken] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null | "loading" | "ok" | "fail"
  const [testMsg, setTestMsg] = useState("");

  function handleSave(e) {
    e?.preventDefault();
    const safe = saveConfig({ apiUrl, adminToken, refreshSeconds });
    onSaved?.(safe);
  }

  function handleReset() {
    if (!confirm("Apagar configurações salvas?")) return;
    clearConfig();
    const fresh = loadConfig();
    setApiUrl(fresh.apiUrl);
    setAdminToken(fresh.adminToken);
    setRefreshSeconds(fresh.refreshSeconds);
    onSaved?.(fresh);
  }

  async function handleTest() {
    setTestStatus("loading");
    setTestMsg("");
    // Salva antes para que o getHealth use a URL atual
    saveConfig({ apiUrl, adminToken, refreshSeconds });
    try {
      const res = await getHealth();
      if (res && res.ok === true) {
        setTestStatus("ok");
        setTestMsg("Backend respondeu /health = { ok: true }.");
      } else {
        setTestStatus("fail");
        setTestMsg(`Resposta inesperada: ${JSON.stringify(res)}`);
      }
    } catch (err) {
      setTestStatus("fail");
      setTestMsg(`${err.message} (status ${err.status ?? "?"})`);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <form onSubmit={handleSave} className="glass-card p-6 space-y-6">
        <header>
          <div className="section-label mb-2">Preferências do NOC</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">{t("nav.settings")}</h1>
          <p className="text-sm text-noc-muted mt-1">
            Ajuste a conexão, a atualização automática e valide a segurança local.
          </p>
        </header>

        <div>
          <label className="label">API URL</label>
          <input
            type="url"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="http://192.168.1.100:8000"
            className="input"
            spellCheck={false}
          />
          <p className="text-xs text-noc-muted mt-1">
            URL base do backend FastAPI. Inclui protocolo e porta.
          </p>
        </div>

        <div>
          <label className="label">Admin Token (X-Admin-Token)</label>
          <div className="flex gap-2">
            <input
              type={showToken ? "text" : "password"}
              value={adminToken}
              onChange={(e) => setAdminToken(e.target.value)}
              placeholder="cole o token gerado em backend/.env"
              className="input font-mono"
              spellCheck={false}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              className="btn-ghost shrink-0"
            >
              {showToken ? "ocultar" : "mostrar"}
            </button>
          </div>
          <p className="text-xs text-noc-muted mt-1">
            Mesmo valor de <span className="kbd">ADMIN_TOKEN</span> em{" "}
            <span className="kbd">backend/.env</span>.
          </p>
        </div>

        <div>
          <label className="label">Auto-refresh (segundos)</label>
          <input
            type="number"
            min={3}
            max={300}
            step={1}
            value={refreshSeconds}
            onChange={(e) => setRefreshSeconds(Number(e.target.value))}
            className="input w-40"
          />
          <p className="text-xs text-noc-muted mt-1">
            Intervalo entre atualizações automáticas. Mínimo 3s, máximo 300s.
          </p>
        </div>

        <div className="pt-3 border-t border-noc-divider flex flex-wrap items-center gap-2">
          <button type="submit" className="btn-primary h-10 px-4">
            {t("settingsPage.save")}
          </button>
          <button type="button" onClick={handleTest} className="btn-ghost h-10 px-4">
            {t("settingsPage.testConnection")}
          </button>
          <div className="flex-1" />
          <button type="button" onClick={handleReset} className="btn-ghost h-10 px-4">
            {t("settingsPage.resetDefaults")}
          </button>
        </div>

        {testStatus && (
          <div
            className={
              testStatus === "ok"
                ? "card border-noc-ok/30 bg-noc-ok/5 p-3 text-sm text-noc-ok"
                : testStatus === "fail"
                  ? "card border-noc-err/30 bg-noc-err/5 p-3 text-sm text-noc-err"
                  : "card p-3 text-sm text-noc-muted"
            }
          >
            {testStatus === "loading" ? t("settingsPage.testing") : testMsg}
          </div>
        )}
      </form>

      {/* SEC-DEFAULTS-WARN — painel de configs de segurança (sanitizado). */}
      <SecuritySummaryCard />

      <div className="mt-6 text-xs text-noc-muted">
        <p>
          <strong>Nota Sprint 1:</strong> token é estático e único para
          todos os operadores. JWT/RBAC entram em Sprint 2+ se múltiplos
          operadores precisarem.
        </p>
      </div>
    </div>
  );
}

// SEC-DEFAULTS-WARN — card read-only que lista avisos de config de segurança.
// Consome GET /api/admin/security/summary (sanitizado; nunca valores de segredo).
function _sevColor(s) {
  return s === "critical" ? "#FF5063" : s === "warning" ? "#FFB224" : "#7e8aa0";
}

function SecuritySummaryCard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("idle"); // idle|loading|ok|error

  const load = () => {
    setState("loading");
    getSecuritySummary()
      .then((d) => { setData(d); setState("ok"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); }, []);

  const statusLabel =
    data?.status === "ok" ? "OK" : data?.status === "critical" ? "Crítico" : "Avisos";

  return (
    <div className="glass-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">Segurança — configuração</h2>
        <button
          type="button"
          className="btn-ghost h-9 px-3 text-xs inline-flex items-center gap-2"
          onClick={load}
          disabled={state === "loading"}
        >
          <NavIcon name="refresh" size={14} />{state === "loading" ? "…" : "Reavaliar"}
        </button>
      </div>

      {state === "error" && (
        <p className="text-sm text-noc-err">
          Não foi possível carregar (requer um Admin Token válido configurado acima).
        </p>
      )}

      {state === "ok" && data && (
        <>
          <div className="text-sm">
            Status:{" "}
            <span
              className="font-semibold"
              style={{ color: _sevColor(data.status === "ok" ? "info" : data.status) }}
            >
              {statusLabel}
            </span>
            <span className="text-noc-muted">
              {" "}· {data.counts.critical} críticos · {data.counts.warning} avisos ·{" "}
              {data.counts.info} info
            </span>
          </div>

          {data.findings.length === 0 ? (
            <p className="text-sm text-noc-ok">✓ Nenhum aviso de configuração.</p>
          ) : (
            <ul className="space-y-2">
              {data.findings.map((f) => (
                <li
                  key={f.code}
                  className="card p-3 text-sm"
                  style={{ borderColor: `${_sevColor(f.severity)}55` }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                      style={{ color: _sevColor(f.severity), border: `1px solid ${_sevColor(f.severity)}` }}
                    >
                      {f.severity}
                    </span>
                    <span className="font-semibold">{f.title}</span>
                  </div>
                  <p className="text-noc-muted mt-1">{f.detail}</p>
                  <p className="text-xs text-noc-text2 mt-1">→ {f.remediation}</p>
                </li>
              ))}
            </ul>
          )}

          <p className="text-[10px] text-noc-muted">
            Somente leitura · nunca exibe valores de segredos · fonte: GET
            /api/admin/security/summary
          </p>
        </>
      )}
    </div>
  );
}
